import { CurrencyPipe, CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Directive, Optional, Host, Input, EventEmitter, Output, HostListener, Pipe, LOCALE_ID, Inject, Injectable, NgModule } from '@angular/core';
import * as i1 from '@angular/material/input';
import * as i1$1 from '@angular/forms';
import { Subject, Observable, of, combineLatest, iif, throwError, timer, fromEvent, merge } from 'rxjs';
import { debounceTime, takeUntil, take, map, delay, finalize, shareReplay, retryWhen, tap, concatMap, distinctUntilChanged, share, filter as filter$1 } from 'rxjs/operators';

class FsAutofocusDirective {
    constructor(_matInput, _el) {
        this._matInput = _matInput;
        this._el = _el;
        this.fsAutofocus = true;
    }
    ngAfterViewInit() {
        if (this.fsAutofocus || this.fsAutofocus === undefined) {
            this.focus();
        }
    }
    focus() {
        setTimeout(() => {
            const tmpEl = document.createElement('input');
            tmpEl.style.width = '0';
            tmpEl.style.height = '0';
            tmpEl.style.margin = '0';
            tmpEl.style.padding = '0';
            tmpEl.style.border = '0';
            tmpEl.style.opacity = '0';
            document.body.appendChild(tmpEl);
            tmpEl.focus();
            if (this._matInput) {
                this._matInput.focus();
            }
            else if (this._el.nativeElement.focus) {
                this._el.nativeElement.focus();
            }
            document.body.removeChild(tmpEl);
        }, 50);
    }
}
FsAutofocusDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsAutofocusDirective, deps: [{ token: i1.MatInput, host: true, optional: true }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
FsAutofocusDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.4.0", type: FsAutofocusDirective, selector: "[fsAutofocus],[autofocus]", inputs: { fsAutofocus: "fsAutofocus" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsAutofocusDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[fsAutofocus],[autofocus]',
                }]
        }], ctorParameters: function () { return [{ type: i1.MatInput, decorators: [{
                    type: Optional
                }, {
                    type: Host
                }] }, { type: i0.ElementRef }]; }, propDecorators: { fsAutofocus: [{
                type: Input
            }] } });

class FsModelChangeDirective {
    constructor(_ngModel) {
        this._ngModel = _ngModel;
        this.fsModelChange = new EventEmitter();
        this._destroy$ = new Subject();
    }
    ngOnInit() {
        this._ngModel.update
            .pipe(debounceTime(this.fsModelChangeOptions.debounce), takeUntil(this._destroy$))
            .subscribe((value) => {
            this.fsModelChange.emit(value);
        });
    }
    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }
}
FsModelChangeDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsModelChangeDirective, deps: [{ token: i1$1.NgModel }], target: i0.ɵɵFactoryTarget.Directive });
FsModelChangeDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.4.0", type: FsModelChangeDirective, selector: "[fsModelChange]", inputs: { fsModelChangeOptions: "fsModelChangeOptions" }, outputs: { fsModelChange: "fsModelChange" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsModelChangeDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[fsModelChange]',
                }]
        }], ctorParameters: function () { return [{ type: i1$1.NgModel }]; }, propDecorators: { fsModelChangeOptions: [{
                type: Input
            }], fsModelChange: [{
                type: Output
            }] } });

class FsPreventDefaultDirective {
    preventClick(event) {
        event.preventDefault();
    }
}
FsPreventDefaultDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsPreventDefaultDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
FsPreventDefaultDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.4.0", type: FsPreventDefaultDirective, selector: "[fsPreventDefault]", host: { listeners: { "click": "preventClick($event)" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsPreventDefaultDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[fsPreventDefault]',
                }]
        }], propDecorators: { preventClick: [{
                type: HostListener,
                args: ['click', ['$event']]
            }] } });

class FsStopPropagationDirective {
    preventClick(event) {
        event.stopPropagation();
    }
}
FsStopPropagationDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsStopPropagationDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
FsStopPropagationDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.4.0", type: FsStopPropagationDirective, selector: "[fsStopPropagation]", host: { listeners: { "click": "preventClick($event)" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsStopPropagationDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[fsStopPropagation]',
                }]
        }], propDecorators: { preventClick: [{
                type: HostListener,
                args: ['click', ['$event']]
            }] } });

function guid(pattern, options = { case: 'lower', numbers: true }) {
    pattern = pattern || 'xxxxxx';
    let s = '';
    if (options.numbers !== false) {
        s = '0123456789';
    }
    if (!options.case || options.case === 'lower' || options.case === 'mixed') {
        s = s.concat('abcdefghijklmnopqrstuvwxyz');
    }
    if (options.case === 'upper' || options.case === 'mixed') {
        s = s.concat('ABCDEFGHIJKLMNOPQRSTUVWXYZ');
    }
    const chars = s.match(/(.{1,1})/g);
    return pattern.replace(/[x]/g, function (c) {
        const i = (Math.random() * chars.length) | 0;
        return chars[i];
    });
}

function isEmpty(value, options) {
    options = options || {};
    return value === undefined ||
        value === null ||
        value === false ||
        value === '' ||
        String(value).length === 0 ||
        (Array.isArray(value) && value.length === 0) ||
        (value instanceof Object && (value.constructor.name === 'Object' && !Object.keys(value).length)) ||
        (!options.zero && (value === 0 || value === '0'));
}

function stringify(value) {
    return JSON.stringify(value);
}

class FsUtilStringifyPipe {
    constructor() { }
    transform(value) {
        return stringify(value);
    }
}
FsUtilStringifyPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsUtilStringifyPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
FsUtilStringifyPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsUtilStringifyPipe, name: "fsUtilStringify" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsUtilStringifyPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'fsUtilStringify',
                }]
        }], ctorParameters: function () { return []; } });
class FsUtilGuidPipe {
    constructor() { }
    transform() {
        return guid();
    }
}
FsUtilGuidPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsUtilGuidPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
FsUtilGuidPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsUtilGuidPipe, name: "fsUtilGuid" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsUtilGuidPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'fsUtilGuid',
                }]
        }], ctorParameters: function () { return []; } });
class FsUtilIsEmptyPipe {
    transform(value) {
        return isEmpty(value);
    }
}
FsUtilIsEmptyPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsUtilIsEmptyPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
FsUtilIsEmptyPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsUtilIsEmptyPipe, name: "fsUtilIsEmpty" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsUtilIsEmptyPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'fsUtilIsEmpty',
                }]
        }] });
class FsUtilIsNotEmptyPipe {
    transform(value) {
        return !isEmpty(value);
    }
}
FsUtilIsNotEmptyPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsUtilIsNotEmptyPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
FsUtilIsNotEmptyPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsUtilIsNotEmptyPipe, name: "fsUtilIsNotEmpty" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsUtilIsNotEmptyPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'fsUtilIsNotEmpty',
                }]
        }] });

function compare(query, item) {
    let value = true;
    for (const key in query) {
        value = value && item[key] == query[key];
    }
    return value;
}

function filter(array, query) {
    if (typeof query !== 'function') {
        const queryObj = query;
        query = (item) => {
            return compare(queryObj, item);
        };
    }
    const isarray = Array.isArray(array);
    const list = isarray ? [] : {};
    if (isarray) {
        array.forEach(function (item, idx) {
            if (query(item)) {
                list.push(item);
            }
        });
    }
    else {
        Object.keys(array).forEach(key => {
            if (query(array[key])) {
                list[key] = array[key];
            }
        });
    }
    return list;
}

function inArray(values, array) {
    if (!Array.isArray(values)) {
        values = [values];
    }
    for (let i = 0, len = values.length; i < len; i++) {
        if (array.indexOf(values[i]) >= 0) {
            return true;
        }
    }
    return false;
}

function index(array, name, value = null) {
    const list = {};
    array.forEach(function (item) {
        list[item[name]] = value ? item[value] : item;
    });
    return list;
}

function indexOf(array, query) {
    if (typeof query !== 'function') {
        const queryObj = query;
        query = (item) => {
            return compare(queryObj, item);
        };
    }
    for (let i = 0, len = array.length; i < len; i++) {
        if (query(array[i])) {
            return i;
        }
    }
    return -1;
}

function keyExists(array, key) {
    return array.hasOwnProperty(key);
}

function ksort(unordered) {
    Object.keys(unordered).sort().forEach(function (key) {
        const value = unordered[key];
        delete unordered[key];
        unordered[key] = value;
    });
}

function list(array, property, index = null) {
    const resultList = index ? {} : [];
    array.forEach(function (item, idx) {
        if (index) {
            resultList[item[index]] = item[property];
        }
        else {
            resultList.push(item[property]);
        }
    });
    return resultList;
}

function nameValue(array, name, value) {
    const list = [];
    if (name || value) {
        const nameFn = typeof name === 'function' ? name : function (item) {
            return item[name];
        };
        const valueFn = typeof value === 'function' ? value : function (item) {
            return item[value];
        };
        array.forEach(function (item) {
            list.push({ name: nameFn(item), value: valueFn(item) });
        });
    }
    else {
        array.forEach(function (n, v) {
            list.push({ name: n, value: v });
        });
    }
    return list;
}

function remove(array, query) {
    const idx = indexOf(array, query);
    if (idx >= 0) {
        return array.splice(idx, 1);
    }
    return idx;
}

function sort(array, query, reverse = false) {
    if (typeof query !== 'function') {
        const queryStr = query;
        query = function (a, b) {
            if (reverse) {
                if (a[queryStr] < b[queryStr]) {
                    return 1;
                }
                else if (a[queryStr] > b[queryStr]) {
                    return -1;
                }
            }
            else {
                if (a[queryStr] > b[queryStr]) {
                    return 1;
                }
                else if (a[queryStr] < b[queryStr]) {
                    return -1;
                }
            }
            return 0;
        };
    }
    array.sort(query);
    return array;
}

function rsort(array, query) {
    return sort(array, query, true);
}

function find(array, query) {
    const data = filter(array, query);
    return data.length ? data[0] : null;
}

function boolean(value) {
    return String(value).toLowerCase() !== 'false' && !!value;
}

function clone(obj) {
    if (obj instanceof Date) {
        return new Date(obj);
    }
    else if (Array.isArray(obj)) {
        const arr = [];
        for (let i = 0; i < obj.length; i++) {
            arr[i] = clone(obj[i]);
        }
        return arr;
    }
    else if (typeof (obj) === 'object') {
        const cloned = {};
        for (let key in obj) {
            cloned[key] = clone(obj[key]);
        }
        return cloned;
    }
    return obj;
}

function debounce(func, wait, immediate) {
    let timeout;
    return function () {
        const context = this, args = arguments;
        const later = function () {
            timeout = null;
            if (!immediate)
                func.apply(context, args);
        };
        const callNow = immediate && !timeout;
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
        if (callNow)
            func.apply(context, args);
    };
}

function removeQueryParams(path) {
    return path.split('?')[0];
}

function getNormalizedPath(locationSrv) {
    const path = locationSrv.prepareExternalUrl(locationSrv.path());
    return removeQueryParams(path);
}

function isArrayEqual(arr1, arr2) {
    if (!Array.isArray(arr1) || !Array.isArray(arr2) || arr1.length !== arr2.length) {
        return false;
    }
    const sortFn = (a, b) => {
        if (a < b) {
            return -1;
        }
        else if (a > b) {
            return 1;
        }
        else {
            return 0;
        }
    };
    arr1 = [...arr1].sort(sortFn);
    arr2 = [...arr2].sort(sortFn);
    return arr1.every((value, index) => {
        return value === arr2[index];
    });
}

function isClass(value, cls) {
    if (typeof value === 'object') {
        if (typeof cls === 'string') {
            if (value.constructor) {
                if (value.constructor.name === cls) {
                    return true;
                }
            }
        }
        else {
            if (value instanceof cls) {
                return true;
            }
        }
    }
    return false;
}

function isNumeric(value) {
    const str = String(value);
    return str.length && !!str.match(/^-?\d*\.?\d*$/);
}

function isObject(value) {
    return !!value && typeof value === 'object' && !Array.isArray(value);
}

const KEY_CANCEL$1 = 3;
const KEY_HELP$1 = 6;
const KEY_BACKSPACE$1 = 8;
const KEY_TAB$1 = 9;
const KEY_CLEAR$1 = 12;
const KEY_RETURN$1 = 13;
const KEY_ENTER$1 = 14;
const KEY_SHIFT$1 = 16;
const KEY_CONTROL$1 = 17;
const KEY_ALT$1 = 18;
const KEY_PAUSE$1 = 19;
const KEY_CAPS_LOCK$1 = 20;
const KEY_ESCAPE$1 = 27;
const KEY_SPACE$1 = 32;
const KEY_PAGE_UP$1 = 33;
const KEY_PAGE_DOWN$1 = 34;
const KEY_END$1 = 35;
const KEY_HOME$1 = 36;
const KEY_LEFT$1 = 37;
const KEY_UP$1 = 38;
const KEY_RIGHT$1 = 39;
const KEY_DOWN$1 = 40;
const KEY_PRINTSCREEN$1 = 44;
const KEY_INSERT$1 = 45;
const KEY_DELETE$1 = 46;
const KEY_0$1 = 48;
const KEY_1$1 = 49;
const KEY_2$1 = 50;
const KEY_3$1 = 51;
const KEY_4$1 = 52;
const KEY_5$1 = 53;
const KEY_6$1 = 54;
const KEY_7$1 = 55;
const KEY_8$1 = 56;
const KEY_9$1 = 57;
const KEY_SEMICOLON$1 = 59;
const KEY_EQUALS$1 = 61;

function length(object) {
    if (Array.isArray(object)) {
        return object.length;
    }
    if (object instanceof Object) {
        return Object.keys(object).length;
    }
    return 0;
}

function ordinalNumber(value) {
    value = String(value);
    const index = value.match `1?.$`;
    const endings = [, 'st', 'nd', 'rd'];
    const ending = endings[index] || 'th';
    return value + ending;
}

class Operation {
    constructor(target, name) {
        this.ready$ = new Subject();
        this.name = name ? name : guid();
        if (target instanceof Observable) {
            this.target = target;
        }
        else {
            this.target = of(target);
        }
    }
}

var QueueState;
(function (QueueState) {
    QueueState["Idle"] = "idle";
    QueueState["Processing"] = "processing";
})(QueueState || (QueueState = {}));

class Queue {
    constructor(_limit = Infinity) {
        this._limit = _limit;
        this._queueStats = {
            completed: 0,
            total: 0,
            errors: [],
        };
        this._doneQueueStats = {
            completed: 0,
            total: 0,
            errors: [],
        };
        this._completeQueueStats = {
            completed: 0,
            total: 0,
            errors: [],
        };
        this._done = new Subject();
        this._queue = [];
        this._inProgress = [];
        this._state = QueueState.Idle;
        this._destroy$ = new Subject();
        // if(_targets) {
        //   _targets.forEach((target) => {
        //     this.push(target);
        //   }); 
        // }
    }
    get total() {
        return this._queueStats.total;
    }
    get completed() {
        return this._queueStats.completed;
    }
    get pending() {
        return this._queue.length;
    }
    get inProgress() {
        return this._inProgress.length;
    }
    get errors() {
        return this._queueStats.errors;
    }
    get state() {
        return this._state;
    }
    get empty() {
        return !this._queue.length && !this._inProgress.length;
    }
    get pendingOperations() {
        return this._queue
            .map((operation) => operation.name);
    }
    get inProgressOperations() {
        return this._inProgress
            .map((operation) => operation.name);
    }
    isProcessing() {
        return this._state === QueueState.Processing;
    }
    isIdle() {
        return this._state === QueueState.Idle;
    }
    setLimit(value) {
        this._limit = value;
    }
    /**
     * @depreated
     */
    subscribe(fun, err, complete) {
        this.observe$
            .subscribe(fun, err, complete);
    }
    get observe$() {
        return this._done
            .pipe(takeUntil(this._destroy$));
    }
    get complete$() {
        if (!this.isProcessing()) {
            return of({ total: 0, completed: 0, errors: [] });
        }
        return this.observe$
            .pipe(take(1), map(() => (this._completeQueueStats)));
    }
    complete(fun, err, complete) {
        this.complete$.subscribe(fun, err, complete);
    }
    push(target, name) {
        const operation = new Operation(target, name);
        this._queueStats.total++;
        this._doneQueueStats.total++;
        this._completeQueueStats.total++;
        this._state = QueueState.Processing;
        if (this._inProgress.length < this._limit) {
            this._processOperation(operation);
        }
        else {
            this._queue.push(operation);
        }
        return operation.ready$;
    }
    clear() {
        this._queue = [];
        this._state = QueueState.Idle;
        this._queueStats = {
            total: 0,
            errors: [],
            completed: 0,
        };
        this._clearDoneQueueStats();
    }
    destroy() {
        this._done.complete();
        this.clear();
    }
    _processOperation(operation) {
        this._inProgress.push(operation);
        operation.target
            .pipe(delay(200), // Hack to prevent extra quick proccess execution
        finalize(() => {
            const opIndex = this._inProgress.indexOf(operation);
            this._inProgress.splice(opIndex, 1);
            if (this.empty) {
                this._state = QueueState.Idle;
                this._done.next(this._doneQueueStats);
                this._clearDoneQueueStats();
            }
            else {
                if (this._queue.length) {
                    const queueItem = this._queue.shift();
                    this._processOperation(queueItem);
                }
            }
        }), takeUntil(this._destroy$)).subscribe({
            next: (data) => {
                operation.ready$.next(data);
            },
            error: (error) => {
                this._queueStats.errors.push(error);
                this._doneQueueStats.errors.push(error);
                this._completeQueueStats.errors.push(error);
                operation.ready$.error(error);
            },
            complete: () => {
                this._queueStats.completed++;
                this._doneQueueStats.completed++;
                this._completeQueueStats.completed++;
                operation.ready$.complete();
            }
        });
    }
    _clearDoneQueueStats() {
        this._doneQueueStats = {
            total: 0,
            errors: [],
            completed: 0,
        };
    }
}

function resolve(promise, defaults = []) {
    const result = defaults;
    return new Promise(resolve => {
        promise.then(function (data) {
            Object.keys(data).forEach(key => result[key] = data[key]);
            resolve(result);
        });
    });
}

function round(num, decimalPlaces = 0) {
    num = parseFloat(parseFloat(num).toPrecision(Number.MAX_SAFE_INTEGER.toString().length - 1));
    const p = Math.pow(10, decimalPlaces);
    return Math.round(num * p) / p;
}

const fsSourceLoader = (function () {
    let _document;
    let _headElement;
    const _sources = new Map();
    const _loadedResources = new Map();
    // bad way but don't want to bother developer
    setDocument(document);
    // but developer can set document at any time
    function setDocument(document) {
        _document = document;
        _headElement = _document?.head;
    }
    /**
     * register a list of the resources for future loading
     *
     * example:
     *   sourceLoader.registerResources({
     *     froala: [
     *       '/assets/js/froala/froala_editor.min.js',
     *       '/assets/css/froala_editor.pkgd.min.css',
     *     ],
     *     froala_align: '/assets/js/froala/plugins/align.min.js',
     *     froala_colors: '/assets/js/froala/plugins/colors.min.js',
     *     froala_image: '/assets/js/froala/plugins/image.min.js',
     *   });
     *
     */
    function registerResources(resources) {
        Object.entries(resources)
            .forEach(([name, path]) => {
            if (typeof path === 'string') {
                path = [path];
            }
            if (!_sources.has(name)) {
                _sources.set(name, new Set());
            }
            const resourcesList = _sources.get(name);
            path.forEach((p) => {
                resourcesList.add(p);
            });
        });
    }
    /**
     * load any resource previously registered
     *
     * sourceLoader.loadResource('froala')
     *
     * or load any js/css file
     *
     * sourceLoader.loadResource('/assets/my-source.js')
     *
     */
    function loadResource(name) {
        if (_loadedResources.has(name)) {
            return _loadedResources.get(name);
        }
        if (_sources.has(name)) {
            const sourcesForLoading = [..._sources.get(name)]
                .map((path) => {
                return _loadResource(path);
            })
                .filter((v) => !!v);
            return combineLatest(sourcesForLoading);
        }
        return _loadResource(name);
    }
    /**
     * the same as `loadResource` but you can pass list of the resources for loading
     */
    function loadResources(listOfResources) {
        const resourcesToLoad = listOfResources
            .reduce((acc, name) => {
            const loading$ = loadResource(name);
            if (loading$) {
                acc.push(loading$);
            }
            return acc;
        }, []);
        return combineLatest(resourcesToLoad);
    }
    ///
    function _loadResource(path) {
        if (_isStylesUrl(path)) {
            return _loadStyles(path);
        }
        if (_isJavascriptUrl(path)) {
            return _loadJs(path);
        }
        console.warn(`Resource ${path} can not be identified and loaded`);
        return null;
    }
    function _loadJs(scriptPath) {
        if (!_loadedResources.has(scriptPath)) {
            const obs$ = new Observable((obs) => {
                const script = document.createElement('script');
                script.src = scriptPath;
                _headElement.appendChild(script);
                script.onload = () => {
                    obs.next();
                    obs.complete();
                };
                script.onerror = (err) => {
                    obs.error(err);
                };
            }).pipe(shareReplay(1));
            _loadedResources.set(scriptPath, obs$);
        }
        return _loadedResources.get(scriptPath);
    }
    function _loadStyles(stylePath) {
        if (!_loadedResources.has(stylePath)) {
            const obs$ = new Observable((obs) => {
                const style = document.createElement('link');
                style.id = stylePath;
                style.rel = 'stylesheet';
                style.href = `${stylePath}`;
                _headElement.appendChild(style);
                style.onload = () => {
                    obs.next();
                    obs.complete();
                };
                style.onerror = (err) => {
                    obs.error(err);
                };
            });
            _loadedResources.set(stylePath, obs$);
        }
        return _loadedResources.get(stylePath);
    }
    function _isJavascriptUrl(url) {
        return url.endsWith('.js');
    }
    function _isStylesUrl(url) {
        return url.endsWith('.css');
    }
    return {
        setDocument,
        registerResources,
        loadResource,
        loadResources,
        loadJs: _loadJs,
        loadStyles: _loadStyles,
    };
})();
function loadStyles(path) {
    return fsSourceLoader.loadStyles(path);
}
;
function loadJs(path) {
    return fsSourceLoader.loadJs(path);
}
;

function throttle(func, wait) {
    let waiting = false; // Initially, we're not waiting
    return function () {
        if (!waiting) { // If we're not waiting
            func.apply(null, arguments); // Execute users function
            waiting = true; // Prevent future invocations
            setTimeout(function () {
                waiting = false; // And allow future invocations
            }, wait);
        }
    };
}

function toString(value) {
    if (value === null || value === undefined) {
        return '';
    }
    return `${value}`;
}

function uuid() {
    return guid('xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx');
}

function value(object, key, def) {
    if (typeof object === 'object' || Array.isArray(object)) {
        if (object.hasOwnProperty(key)) {
            return object[key];
        }
    }
    return def;
}

function number(value, decimals) {
    if (isNumeric(decimals)) {
        value = round(value, decimals);
    }
    return Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: decimals });
}

function acronym(str, length = 2) {
    const splittedString = str
        .replace(/[^a-zA-Z0-9]+/g, ' ')
        .split(' ');
    let result = '';
    for (let i = 0; i < length; i++) {
        if (!splittedString[i]) {
            if (splittedString.length === 1) {
                for (let j = 1; j < length; j++) {
                    if (!splittedString[i - 1][j]) {
                        break;
                    }
                    result += splittedString[i - 1][j].toUpperCase();
                }
            }
            break;
        }
        result += splittedString[i][0].toUpperCase();
    }
    return result;
}

function currency(amount, precision = [2, 10], currencyCode = 'USD', locale = 'en-US') {
    const digitsInfo = Array.isArray(precision) ? precision : [precision, precision];
    amount = round(amount, digitsInfo[1]);
    return new CurrencyPipe(locale)
        .transform(amount, currencyCode, 'symbol', `1.${digitsInfo[0]}-${digitsInfo[1]}`);
}

function truncate(value, limit = 100, completeWords = false, ellipsis = '...') {
    value = value || '';
    if (completeWords) {
        limit = value.substr(0, limit).lastIndexOf(' ');
    }
    let result = `${value.substr(0, limit)}`;
    if (result !== value) {
        result += ellipsis;
    }
    return result;
}

function email(value) {
    return !!String(value).match(/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);
}

function phone(value) {
    const valid = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4}/.test(value);
    return valid || !String(value).length;
}

/**
 * Check validity of the url.
 * Protocol (http/https) isn't required.
 * @param value
 * @returns {boolean}
 */
function url(value) {
    const expression = /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#%[\]@!\$&'\(\)\*\+,;=.]+$/gm;
    const regexp = new RegExp(expression);
    return !!String(value).match(regexp);
}

function delayedRetry(delayMs, maxRetry = 5, message) {
    let retries = 0;
    return (src) => src.pipe(retryWhen((errors) => errors.pipe(tap(() => {
        retries++;
        if (retries <= maxRetry) {
            console.info(`Failed to load Resource. Retry in ${delayMs / 1000} sec. ${retries}/${maxRetry} times...`);
        }
    }), concatMap((error) => iif(() => retries > maxRetry, throwError(getErrorMessage(maxRetry, message)), timer(delayMs))))));
}
const getErrorMessage = (maxRetry, defaultMessage) => {
    if (!defaultMessage) {
        return `Failed to load Resource for ${maxRetry} times. Giving up.`;
    }
    else {
        return defaultMessage;
    }
};

function bytes(bytes, decimals, unit) {
    if (!bytes) {
        return '0 MB';
    }
    const e = typeof unit === 'string' ?
        'KMGTP'.indexOf(unit.replace('B', '')) + 1 :
        Math.floor(Math.log(bytes) / Math.log(1024));
    if (!isNumeric(decimals)) {
        decimals = (bytes / 1024) > 1000 ? 2 : 0;
    }
    return `${(bytes / Math.pow(1024, e))
        .toFixed(decimals)}`
        .replace(/\.00/, '')
        + ' '
        + (e ? ' KMGTP'.charAt(e) : '') + 'B';
}

class FsFormatBytesPipe {
    transform(value, decimals, unit) {
        return bytes(value, decimals, unit);
    }
}
FsFormatBytesPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsFormatBytesPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
FsFormatBytesPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsFormatBytesPipe, name: "fsFormatBytes" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsFormatBytesPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'fsFormatBytes',
                }]
        }] });

class FsFormatCurrencyPipe {
    constructor(locale) {
        this.locale = locale;
    }
    transform(amount, precision = 2, currencyCode) {
        return currency(amount, precision, currencyCode);
    }
}
FsFormatCurrencyPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsFormatCurrencyPipe, deps: [{ token: LOCALE_ID }], target: i0.ɵɵFactoryTarget.Pipe });
FsFormatCurrencyPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsFormatCurrencyPipe, name: "fsFormatCurrency" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsFormatCurrencyPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'fsFormatCurrency',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [LOCALE_ID]
                }] }]; } });

class FsFormatNumberPipe {
    transform(value, decimals) {
        return number(value, decimals);
    }
}
FsFormatNumberPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsFormatNumberPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
FsFormatNumberPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsFormatNumberPipe, name: "fsFormatNumber" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsFormatNumberPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'fsFormatNumber'
                }]
        }] });

class FsFormatOrdinalNumberPipe {
    transform(value) {
        return ordinalNumber(value);
    }
}
FsFormatOrdinalNumberPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsFormatOrdinalNumberPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
FsFormatOrdinalNumberPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsFormatOrdinalNumberPipe, name: "fsFormatOrdinalNumber" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsFormatOrdinalNumberPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'fsFormatOrdinalNumber',
                }]
        }] });

class FsFormatTruncatePipe {
    constructor() { }
    transform(value, limit, completeWords, ellipsis) {
        return truncate(value, limit, completeWords, ellipsis);
    }
}
FsFormatTruncatePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsFormatTruncatePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
FsFormatTruncatePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsFormatTruncatePipe, name: "fsFormatTruncate" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsFormatTruncatePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'fsFormatTruncate'
                }]
        }], ctorParameters: function () { return []; } });

class FsArray {
    /**
     * @deprecated use import { nameValue } from @firestitch/common/array; instead
    */
    nameValue(array, name, value) {
        console.warn('@deprecated use import { nameValue } from @firestitch/common/array; instead');
        let list = [];
        if (name || value) {
            let nameFn = typeof name === 'function' ? name : function (item) { return item[name]; };
            let valueFn = typeof value === 'function' ? value : function (item) { return item[value]; };
            array.forEach(function (item) {
                list.push({ name: nameFn(item), value: valueFn(item) });
            });
        }
        else {
            array.forEach(function (name, value) {
                list.push({ name: name, value: value });
            });
        }
        return list;
    }
    /**
     * @deprecated use import { remove } from @firestitch/common/array; instead
    */
    remove(array, query) {
        console.warn('@deprecated use import { remove } from @firestitch/common/array; instead');
        let idx = this.indexOf(array, query);
        if (idx >= 0) {
            return array.splice(idx, 1);
        }
        return idx;
    }
    /**
     * @deprecated use import { indexOf } from @firestitch/common/array; instead
    */
    indexOf(array, query) {
        console.warn('@deprecated use import { indexOf } from @firestitch/common/array; instead');
        if (typeof query !== 'function') {
            let queryObj = query;
            query = (item) => {
                return this.compare(queryObj, item);
            };
        }
        for (let i = 0, len = array.length; i < len; i++) {
            if (query(array[i])) {
                return i;
            }
        }
        return -1;
    }
    /**
     * @deprecated use import { compare } from @firestitch/common/array; instead
    */
    compare(query, item) {
        console.warn('@deprecated use import { compare } from @firestitch/common/array; instead');
        let value = true;
        for (let key in query) {
            value = value && item[key] == query[key];
        }
        return value;
    }
    /**
     * @deprecated use import { filter } from @firestitch/common/array; instead
    */
    filter(array, query) {
        console.warn('@deprecated use import { filter } from @firestitch/common/array; instead');
        if (typeof query !== 'function') {
            let queryObj = query;
            query = (item) => {
                return this.compare(queryObj, item);
            };
        }
        let isarray = Array.isArray(array);
        let list = isarray ? [] : {};
        if (isarray)
            array.forEach(function (item, idx) {
                if (query(item)) {
                    list.push(item);
                }
            });
        else
            Object.keys(array).forEach(key => {
                if (query(array[key])) {
                    list[key] = array[key];
                }
            });
        return list;
    }
    /**
     * @deprecated use import { index } from @firestitch/common/array; instead
    */
    index(array, property) {
        console.warn('@deprecated use import { index } from @firestitch/common/array; instead');
        let list = {};
        array.forEach(function (item, idx) {
            list[item[property]] = item;
        });
        return list;
    }
    /**
     * @deprecated use import { sort } from @firestitch/common/array; instead
    */
    sort(array, query, reverse = false) {
        console.warn('@deprecated use import { sort } from @firestitch/common/array; instead');
        if (typeof query !== 'function') {
            let queryStr = query;
            query = function (a, b) {
                if (reverse) {
                    if (a[queryStr] < b[queryStr]) {
                        return 1;
                    }
                    else if (a[queryStr] > b[queryStr]) {
                        return -1;
                    }
                }
                else {
                    if (a[queryStr] > b[queryStr]) {
                        return 1;
                    }
                    else if (a[queryStr] < b[queryStr]) {
                        return -1;
                    }
                }
                return 0;
            };
        }
        array.sort(query);
        return array;
    }
    /**
     * @deprecated use import { rsort } from @firestitch/common/array; instead
    */
    rsort(array, query) {
        console.warn('@deprecated use import { rsort } from @firestitch/common/array; instead');
        return this.sort(array, query, true);
    }
    /**
     * @deprecated use import { list } from @firestitch/common/array; instead
    */
    list(array, property, index = null) {
        console.warn('@deprecated use import { list } from @firestitch/common/array; instead');
        let list = index ? {} : [];
        array.forEach(function (item, idx) {
            if (index) {
                list[item[index]] = item[property];
            }
            else {
                list.push(item[property]);
            }
        });
        return list;
    }
    /**
     * @deprecated use import { applyDepth } from @firestitch/common/array; instead
    */
    applyDepth(objects, parent_property, id_property = 'id', depth_property = 'depth') {
        console.warn('@deprecated use import { applyDepth } from @firestitch/common/array; instead');
        let keyed = {};
        objects.forEach(function (object) {
            if (!object[parent_property]) {
                object[depth_property] = 0;
            }
            keyed[object[id_property]] = object;
        });
        Object.keys(keyed).forEach(key => {
            Object.keys(keyed).forEach(key => {
                let object = keyed[key];
                if (!keyed[key][depth_property]) {
                    if (keyed[key][parent_property]) {
                        keyed[key][depth_property] = keyed[keyed[key][parent_property]][depth_property] + 1;
                    }
                }
            });
        });
        return keyed;
    }
    /**
     * @deprecated use import { inArray } from @firestitch/common/array; instead
    */
    inArray(values, array) {
        console.warn('@deprecated use import { inArray } from @firestitch/common/array; instead');
        if (!Array.isArray(values)) {
            values = [values];
        }
        for (let i = 0, len = values.length; i < len; i++) {
            if (array.indexOf(values[i]) >= 0) {
                return true;
            }
        }
        return false;
    }
    /**
     * @deprecated use import { keyExists } from @firestitch/common/array; instead
    */
    keyExists(array, key) {
        console.warn('@deprecated use import { keyExists } from @firestitch/common/array; instead');
        return array.hasOwnProperty(key);
    }
    /**
     * @deprecated use import { length } from @firestitch/common/array; instead
    */
    length(array) {
        console.warn('@deprecated use import { length } from @firestitch/common/array; instead');
        return array.length;
    }
    /**
     * @deprecated use import { ksort } from @firestitch/common/array; instead
    */
    ksort(unordered) {
        console.warn('@deprecated use import { ksort } from @firestitch/common/array; instead');
        Object.keys(unordered).sort().forEach(function (key) {
            let value = unordered[key];
            delete unordered[key];
            unordered[key] = value;
        });
    }
}
FsArray.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsArray, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
FsArray.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsArray });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsArray, decorators: [{
            type: Injectable
        }] });

class FsMath {
    /**
     * @deprecated use _.round() instead {@link https://lodash.com/docs/4.17.5#round}
    */
    round(number, precision) {
        console.warn('@deprecated use _.round() instead {@link https://lodash.com/docs/4.17.5#round}');
        precision = precision || 0;
        let factor = Math.pow(10, precision);
        let tempNumber = number * factor;
        let roundedTempNumber = Math.round(tempNumber);
        return roundedTempNumber / factor;
    }
}
FsMath.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsMath, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
FsMath.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsMath });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsMath, decorators: [{
            type: Injectable
        }] });

const KEY_CANCEL = 3, KEY_HELP = 6, KEY_BACKSPACE = 8, KEY_TAB = 9, KEY_CLEAR = 12, KEY_RETURN = 13, KEY_ENTER = 14, KEY_SHIFT = 16, KEY_CONTROL = 17, KEY_ALT = 18, KEY_PAUSE = 19, KEY_CAPS_LOCK = 20, KEY_ESCAPE = 27, KEY_SPACE = 32, KEY_PAGE_UP = 33, KEY_PAGE_DOWN = 34, KEY_END = 35, KEY_HOME = 36, KEY_LEFT = 37, KEY_UP = 38, KEY_RIGHT = 39, KEY_DOWN = 40, KEY_PRINTSCREEN = 44, KEY_INSERT = 45, KEY_DELETE = 46, KEY_0 = 48, KEY_1 = 49, KEY_2 = 50, KEY_3 = 51, KEY_4 = 52, KEY_5 = 53, KEY_6 = 54, KEY_7 = 55, KEY_8 = 56, KEY_9 = 57, KEY_SEMICOLON = 59, KEY_EQUALS = 61;
/*
    Basically, just copy-pasted functionality from fs-boilerplate v1.x, with some small changes (like we dont have angular.bind anymore, had to go with a workaround)
    There might be a better way to organise constatns but I need to know use-cases
*/
class FsUtil {
    constructor() {
        this.intervals = {};
    }
    /**
    * @deprecated use @firestitch/common/util/guid instead
    */
    guid(pattern) {
        console.warn('@deprecated use @firestitch/common/util/guid instead');
        pattern = pattern || 'xxxxxx';
        return pattern.replace(/[xy]/g, function (c) {
            var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }
    /**
     * @deprecated use import { uuid } from @firestitch/common/util; instead
    */
    uuid() {
        console.warn('@deprecated use @firestitch/common/util/uuid instead');
        return this.guid('xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx');
    }
    /**
     * @deprecated use import { resolve } from @firestitch/common/util; instead
    */
    resolve(promise, defaults = []) {
        console.warn('@deprecated use @firestitch/common/util/resolve instead');
        const result = defaults;
        return new Promise(resolve => {
            promise.then(function (data) {
                Object.keys(data).forEach(key => result[key] = data[key]);
                resolve(result);
            });
        });
    }
    /**
     * @deprecated use _.toInteger() instead
    */
    int(value) {
        console.warn('@deprecated use _.toInteger() instead');
        value = parseInt(value);
        if (isNaN(value)) {
            value = 0;
        }
        return value;
    }
    /**
     * @deprecated use _.toFinite() instead
    */
    float(value) {
        console.warn('@deprecated use _.toFinite() instead');
        value = parseFloat(value);
        if (isNaN(value)) {
            value = 0;
        }
        return value;
    }
    /**
     * @deprecated use _.toString() instead
    */
    string(string) {
        console.warn('@deprecated use _.toString() instead');
        if (string === null || string === undefined) {
            string = '';
        }
        return (string).toString();
    }
    /**
     * @deprecated use import { isEmpty } from @firestitch/common/util; instead
    */
    isEmpty(value, options) {
        console.warn('@deprecated use import { isEmpty } from @firestitch/common/util; instead');
        options = options || {};
        return value === undefined ||
            value === null ||
            value === false ||
            value === '' ||
            !this.string(value).length ||
            (this.isObject(value) &&
                (value.constructor.name === 'Object' && !Object.keys(value).length)) ||
            (!options.zero && (value === 0 || value === '0'));
    }
    /**
     * @deprecated use _.isInteger() instead
    */
    isInt(value, type) {
        console.warn('@deprecated use _.isInteger() instead');
        const int = !!this.string(value).match(/^\d+$/);
        if (!int) {
            return false;
        }
        if (type) {
            return Number.isInteger(value);
        }
        return true;
    }
    /**
     * @deprecated use _.each() instead {@link https://lodash.com/docs/4.17.5#forEach}
    */
    each(object, func) {
        console.warn('@deprecated use _.each() instead {@link https://lodash.com/docs/4.17.5#forEach}');
        if (!this.isArray(object) && !this.isObject(object)) {
            return;
        }
        Object.keys(object).forEach(function (key) {
            func(object[key], key);
        });
    }
    /**
     * @deprecated use _.isArrayLikeObject() {@link https://lodash.com/docs/4.17.5#isArrayLikeObject}
     * or _.isObject() {@link https://lodash.com/docs/4.17.5#isObject} instead
    */
    isObject(value) {
        console.warn('@deprecated use _.isArrayLikeObject() {@link https://lodash.com/docs/4.17.5#isArrayLikeObject} or _.isObject() {@link https://lodash.com/docs/4.17.5#isObject} instead');
        return value !== null && typeof value === 'object' && !this.isArray(value);
    }
    /**
      * @deprecated use _.isString() instead {@link https://lodash.com/docs/4.17.5#isString}
    */
    isString(value) {
        console.warn('@deprecated use _.isString() instead {@link https://lodash.com/docs/4.17.5#isString}');
        return typeof value === 'string' || value instanceof String;
    }
    /**
     * @deprecated use _.isArray() instead {@link https://lodash.com/docs/4.17.5#isArray}
    */
    isArray(value) {
        console.warn('@deprecated use _.isArray() instead {@link https://lodash.com/docs/4.17.5#isArray}');
        return value instanceof Array;
    }
    /**
     * @deprecated use import { isNumeric } from @firestitch/common/util; instead
    */
    isNumeric(value) {
        console.warn('@deprecated use import { isNumeric } from @firestitch/common/util; instead');
        return this.string(value).length && !!this.string(value).match(/^-?\d*\.?\d*$/);
    }
    /**
     * @deprecated use import { isClass } from @firestitch/common/util; instead
    */
    isClass(value, cls) {
        console.warn('@deprecated use import { isClass } from @firestitch/common/util; instead');
        if (this.isObject(value)) {
            if (this.isString(cls)) {
                if (value.constructor) {
                    if (value.constructor.name === cls) {
                        return true;
                    }
                }
            }
            else {
                if (value instanceof cls) {
                    return true;
                }
            }
        }
        return false;
    }
    /**
      * @deprecated use _.isBoolean() instead {@link https://lodash.com/docs/4.17.5#isBoolean}
    */
    isBoolean(value) {
        console.warn('@deprecated use _.isBoolean() instead {@link https://lodash.com/docs/4.17.5#isBoolean}');
        return value === true || value === false;
    }
    /**
     * @deprecated use import { value } from @firestitch/common/util; instead
    */
    value(object, key, def) {
        console.warn('@deprecated use import { value } from @firestitch/common/util; instead');
        if (this.isObject(object) || this.isArray(object)) {
            if (object.hasOwnProperty(key)) {
                return object[key];
            }
        }
        return def;
    }
    throttle(func, wait) {
        console.warn('@deprecated use import { throttle } from @firestitch/common/util; instead');
        let waiting = false; // Initially, we're not waiting
        return function () {
            if (!waiting) { // If we're not waiting
                func.apply(null, arguments); // Execute users function
                waiting = true; // Prevent future invocations
                setTimeout(function () {
                    waiting = false; // And allow future invocations
                }, wait);
            }
        };
    }
    /**
     * @deprecated use import { debounce } from @firestitch/common/util; instead
    */
    debounce(func, wait, immediate) {
        console.warn('@deprecated use import { debounce } from @firestitch/common/util; instead');
        var timeout;
        return function () {
            var context = this, args = arguments;
            var later = function () {
                timeout = null;
                if (!immediate)
                    func.apply(context, args);
            };
            var callNow = immediate && !timeout;
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
            if (callNow)
                func.apply(context, args);
        };
    }
    /**
     * @deprecated use import { length } from @firestitch/common/util; instead
    */
    length(object) {
        console.warn('@deprecated use import { length } from @firestitch/common/util; instead');
        if (this.isObject(object)) {
            return Object.keys(object).length;
        }
        if (this.isArray(object)) {
            return object.length;
        }
        return 0;
    }
    /**
     * @deprecated use import { boolean } from @firestitch/common/util; instead
    */
    boolean(value) {
        console.warn('@deprecated use import { boolean } from @firestitch/common/util; instead');
        return this.string(value).toLowerCase() !== 'false' && !!value;
    }
    /**
     * @deprecated use import { stringify } from @firestitch/common/util; instead
    */
    stringify(value) {
        console.warn('@deprecated use import { stringify } from @firestitch/common/util; instead');
        return JSON.stringify(value);
    }
}
FsUtil.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsUtil, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
FsUtil.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsUtil });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsUtil, decorators: [{
            type: Injectable
        }] });

class FsValidate {
    constructor(fsUtil) {
        this.fsUtil = fsUtil;
    }
    /**
     * @deprecated use import { email } from @firestitch/common/validate; instead
    */
    phone(value) {
        const valid = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/.test(value);
        return valid || !String(value).length;
    }
    /**
      * @deprecated use import { email } from @firestitch/common/validate; instead
    */
    email(value) {
        return !!this.fsUtil.string(value).match(/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);
    }
}
FsValidate.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsValidate, deps: [{ token: FsUtil }], target: i0.ɵɵFactoryTarget.Injectable });
FsValidate.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsValidate });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsValidate, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: FsUtil }]; } });

class FsCommonModule {
    static forRoot() {
        return {
            ngModule: FsCommonModule,
            providers: [
                FsArray,
                FsMath,
                FsUtil,
                FsValidate,
            ],
        };
    }
}
FsCommonModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsCommonModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FsCommonModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsCommonModule, declarations: [FsStopPropagationDirective,
        FsPreventDefaultDirective,
        FsAutofocusDirective,
        FsUtilGuidPipe,
        FsUtilIsEmptyPipe,
        FsUtilIsNotEmptyPipe,
        FsUtilStringifyPipe,
        FsFormatNumberPipe,
        FsFormatCurrencyPipe,
        FsFormatTruncatePipe,
        FsFormatBytesPipe,
        FsFormatOrdinalNumberPipe,
        FsModelChangeDirective], imports: [CommonModule], exports: [FsStopPropagationDirective,
        FsPreventDefaultDirective,
        FsAutofocusDirective,
        FsUtilGuidPipe,
        FsUtilStringifyPipe,
        FsFormatNumberPipe,
        FsFormatCurrencyPipe,
        FsFormatTruncatePipe,
        FsUtilIsEmptyPipe,
        FsUtilIsNotEmptyPipe,
        FsFormatBytesPipe,
        FsModelChangeDirective,
        FsFormatOrdinalNumberPipe] });
FsCommonModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsCommonModule, imports: [[
            CommonModule,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsCommonModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                    ],
                    declarations: [
                        FsStopPropagationDirective,
                        FsPreventDefaultDirective,
                        FsAutofocusDirective,
                        FsUtilGuidPipe,
                        FsUtilIsEmptyPipe,
                        FsUtilIsNotEmptyPipe,
                        FsUtilStringifyPipe,
                        FsFormatNumberPipe,
                        FsFormatCurrencyPipe,
                        FsFormatTruncatePipe,
                        FsFormatBytesPipe,
                        FsFormatOrdinalNumberPipe,
                        FsModelChangeDirective,
                    ],
                    exports: [
                        FsStopPropagationDirective,
                        FsPreventDefaultDirective,
                        FsAutofocusDirective,
                        FsUtilGuidPipe,
                        FsUtilStringifyPipe,
                        FsFormatNumberPipe,
                        FsFormatCurrencyPipe,
                        FsFormatTruncatePipe,
                        FsUtilIsEmptyPipe,
                        FsUtilIsNotEmptyPipe,
                        FsFormatBytesPipe,
                        FsModelChangeDirective,
                        FsFormatOrdinalNumberPipe,
                    ],
                }]
        }] });

const keyboardShortcut = (shortcut) => {
    // Observables for all keydown and keyup events
    const keyDown$ = fromEvent(document, 'keydown');
    const keyUp$ = fromEvent(document, 'keyup');
    // All KeyboardEvents - emitted only when KeyboardEvent changes (key or type)
    const keyEvents$ = merge(keyDown$, keyUp$).pipe(distinctUntilChanged((a, b) => a.code === b.code && a.type === b.type), share());
    // Create KeyboardEvent Observable for specified KeyCode
    const createKeyPressStream = (charCode) => keyEvents$.pipe(filter$1((event) => event.keyCode === charCode));
    // Create Event Stream for every KeyCode in shortcut
    const keyCodeEvents$ = shortcut.map((s) => createKeyPressStream(s));
    // Emit when specified keys are pressed (keydown).
    // Emit only when all specified keys are pressed at the same time.
    // More on combineLatest below
    return combineLatest(keyCodeEvents$).pipe(filter$1((arr) => arr.every((a) => a.type === 'keydown')));
};

// Modules

/**
 * Generated bundle index. Do not edit.
 */

export { FsArray, FsAutofocusDirective, FsCommonModule, FsFormatBytesPipe, FsFormatCurrencyPipe, FsFormatNumberPipe, FsFormatOrdinalNumberPipe, FsFormatTruncatePipe, FsMath, FsModelChangeDirective, FsPreventDefaultDirective, FsStopPropagationDirective, FsUtil, FsUtilGuidPipe, FsUtilIsEmptyPipe, FsUtilIsNotEmptyPipe, FsUtilStringifyPipe, FsValidate, KEY_0$1 as KEY_0, KEY_1$1 as KEY_1, KEY_2$1 as KEY_2, KEY_3$1 as KEY_3, KEY_4$1 as KEY_4, KEY_5$1 as KEY_5, KEY_6$1 as KEY_6, KEY_7$1 as KEY_7, KEY_8$1 as KEY_8, KEY_9$1 as KEY_9, KEY_ALT$1 as KEY_ALT, KEY_BACKSPACE$1 as KEY_BACKSPACE, KEY_CANCEL$1 as KEY_CANCEL, KEY_CAPS_LOCK$1 as KEY_CAPS_LOCK, KEY_CLEAR$1 as KEY_CLEAR, KEY_CONTROL$1 as KEY_CONTROL, KEY_DELETE$1 as KEY_DELETE, KEY_DOWN$1 as KEY_DOWN, KEY_END$1 as KEY_END, KEY_ENTER$1 as KEY_ENTER, KEY_EQUALS$1 as KEY_EQUALS, KEY_ESCAPE$1 as KEY_ESCAPE, KEY_HELP$1 as KEY_HELP, KEY_HOME$1 as KEY_HOME, KEY_INSERT$1 as KEY_INSERT, KEY_LEFT$1 as KEY_LEFT, KEY_PAGE_DOWN$1 as KEY_PAGE_DOWN, KEY_PAGE_UP$1 as KEY_PAGE_UP, KEY_PAUSE$1 as KEY_PAUSE, KEY_PRINTSCREEN$1 as KEY_PRINTSCREEN, KEY_RETURN$1 as KEY_RETURN, KEY_RIGHT$1 as KEY_RIGHT, KEY_SEMICOLON$1 as KEY_SEMICOLON, KEY_SHIFT$1 as KEY_SHIFT, KEY_SPACE$1 as KEY_SPACE, KEY_TAB$1 as KEY_TAB, KEY_UP$1 as KEY_UP, Queue, QueueState, acronym, boolean, bytes, clone, compare, currency, debounce, delayedRetry, email, filter, find, fsSourceLoader, getNormalizedPath, guid, inArray, index, indexOf, isArrayEqual, isClass, isEmpty, isNumeric, isObject, keyExists, keyboardShortcut, ksort, length, list, loadJs, loadStyles, nameValue, number, ordinalNumber, phone, remove, removeQueryParams, resolve, round, rsort, sort, stringify, throttle, toString, truncate, url, uuid, value };
